'use client';

import { cn } from '@/lib/utils';

export function HeaderV2Skeleton() {
  return (
    <header className="sticky top-0 z-50 flex justify-between items-center px-4 md:px-6 py-4 border-b border-[#30363d] bg-[#0d1117]">
      <div className="h-7 w-32 bg-[#30363d] rounded animate-pulse" />
      <div className="flex items-center gap-4">
        <div className="h-9 w-28 bg-[#30363d] rounded-full animate-pulse" />
        <div className="h-9 w-9 bg-[#30363d] rounded-full animate-pulse" />
      </div>
    </header>
  );
}

export function MarketBriefingSkeleton() {
  return (
    <section className="py-5 px-4 md:px-6 border-b border-[#30363d]">
      <div className="flex justify-between items-center mb-4">
        <div className="h-4 w-48 bg-[#30363d] rounded animate-pulse" />
        <div className="h-4 w-40 bg-[#30363d] rounded animate-pulse" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="p-4 rounded-xl bg-[#1c2128] border border-[#30363d] animate-pulse">
            <div className="h-7 w-7 bg-[#30363d] rounded mb-2" />
            <div className="h-5 w-24 bg-[#30363d] rounded mb-1" />
            <div className="h-3 w-32 bg-[#30363d] rounded mb-2" />
            <div className="h-3 w-28 bg-[#30363d] rounded" />
          </div>
        ))}
      </div>
    </section>
  );
}

export function AudioPlayerSkeleton() {
  return (
    <section className="py-5 px-4 md:px-6 border-b border-[#30363d]">
      <div className="bg-[#1c2128] rounded-2xl p-5 border border-[#30363d] animate-pulse">
        <div className="flex justify-between items-start mb-4">
          <div className="flex-1">
            <div className="h-5 w-64 bg-[#30363d] rounded mb-2" />
            <div className="h-4 w-48 bg-[#30363d] rounded" />
          </div>
          <div className="h-6 w-12 bg-[#30363d] rounded-xl" />
        </div>
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-[#30363d] rounded-full" />
          <div className="flex-1">
            <div className="h-1.5 bg-[#30363d] rounded-full" />
            <div className="flex justify-between mt-1.5">
              <div className="h-3 w-8 bg-[#30363d] rounded" />
              <div className="h-3 w-8 bg-[#30363d] rounded" />
            </div>
          </div>
          <div className="h-8 w-10 bg-[#30363d] rounded-lg" />
        </div>
      </div>
    </section>
  );
}

export function TopStoriesSkeleton() {
  return (
    <section className="bg-[#1c2128] rounded-2xl p-6 border border-[#30363d] animate-pulse">
      <div className="flex justify-between items-center mb-5">
        <div className="h-4 w-24 bg-[#30363d] rounded" />
        <div className="h-5 w-32 bg-[#30363d] rounded-xl" />
      </div>
      <div className="p-5 bg-[#161b22] rounded-xl border-l-[3px] border-[#30363d]">
        <div className="h-3 w-24 bg-[#30363d] rounded mb-3" />
        <div className="h-6 w-3/4 bg-[#30363d] rounded mb-3" />
        <div className="space-y-2">
          <div className="h-4 w-full bg-[#30363d] rounded" />
          <div className="h-4 w-5/6 bg-[#30363d] rounded" />
          <div className="h-4 w-4/5 bg-[#30363d] rounded" />
        </div>
      </div>
      <div className="flex justify-center gap-2 mt-5">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className={cn('h-2 bg-[#30363d] rounded-full', i === 1 ? 'w-6' : 'w-2')} />
        ))}
      </div>
    </section>
  );
}

export function SentimentSidebarSkeleton() {
  return (
    <aside className="bg-[#1c2128] rounded-2xl p-6 border border-[#30363d] animate-pulse">
      <div className="flex justify-between items-center mb-5">
        <div className="h-4 w-20 bg-[#30363d] rounded" />
        <div className="flex gap-2">
          <div className="h-8 w-16 bg-[#30363d] rounded-full" />
          <div className="h-8 w-24 bg-[#30363d] rounded-full" />
        </div>
      </div>
      <div className="flex flex-col items-center mb-6">
        <div className="w-36 h-36 bg-[#30363d] rounded-full mb-2" />
        <div className="h-3 w-24 bg-[#30363d] rounded" />
      </div>
      <div className="space-y-3">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="flex items-start gap-3 p-3 rounded-lg">
            <div className="h-7 w-20 bg-[#30363d] rounded-lg" />
            <div className="flex-1">
              <div className="h-4 w-16 bg-[#30363d] rounded mb-2" />
              <div className="h-3 w-full bg-[#30363d] rounded" />
            </div>
          </div>
        ))}
      </div>
    </aside>
  );
}

export function DetailsSkeleton() {
  return (
    <section className="mt-6">
      <div className="flex gap-2 mb-5">
        <div className="h-10 w-24 bg-[#30363d] rounded-lg animate-pulse" />
        <div className="h-10 w-16 bg-[#30363d] rounded-lg animate-pulse" />
      </div>
      <div className="space-y-4">
        {[1, 2].map((i) => (
          <div key={i} className="bg-[#161b22] rounded-xl p-5 animate-pulse">
            <div className="flex justify-between items-center mb-3">
              <div className="h-4 w-20 bg-[#30363d] rounded" />
              <div className="h-3 w-8 bg-[#30363d] rounded" />
            </div>
            <div className="flex justify-between items-center mb-3">
              <div className="flex gap-2">
                <div className="h-5 w-12 bg-[#30363d] rounded" />
                <div className="h-5 w-24 bg-[#30363d] rounded-full" />
              </div>
              <div className="h-8 w-20 bg-[#30363d] rounded-full" />
            </div>
            <div className="space-y-2">
              <div className="h-4 w-full bg-[#30363d] rounded" />
              <div className="h-4 w-5/6 bg-[#30363d] rounded" />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export function PageV2LoadingSkeleton() {
  return (
    <div className="min-h-screen bg-[#0d1117]">
      <HeaderV2Skeleton />
      <MarketBriefingSkeleton />
      <AudioPlayerSkeleton />
      <main className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-6 p-6 max-w-[1400px] mx-auto">
        <div>
          <TopStoriesSkeleton />
          <DetailsSkeleton />
        </div>
        <SentimentSidebarSkeleton />
      </main>
    </div>
  );
}
